import { Video, X } from "lucide-react"
import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

type CameraClearButtonProps = {
  title: string
  onClick: () => void
  className?: string
}

/** Small corner X to clear a camera selection (segment + prompt toolbar). */
export function CameraClearButton({ title, onClick, className }: CameraClearButtonProps) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      className={cn(
        "absolute -top-[0.15rem] -right-[0.15rem] inline-flex h-[0.9rem] w-[0.9rem] cursor-pointer items-center justify-center rounded-full",
        "border border-ui-border bg-ui-surface-raised text-ui-text-muted transition-[color,border-color] duration-150",
        "hover:border-[color-mix(in_srgb,var(--ui-status-failed)_40%,transparent)] hover:text-ui-status-failed",
        className,
      )}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      <X className="h-2.5 w-2.5" strokeWidth={2.5} />
    </button>
  )
}

type CameraPickButtonProps = {
  label: string
  title?: string
  onClick: () => void
  /** Extra right padding so label does not sit under the clear badge. */
  reserveClearSpace?: boolean
  className?: string
}

/** Shared camera pick control — same size/style in segment header and prompt toolbar. */
export function CameraPickButton({
  label,
  title,
  onClick,
  reserveClearSpace = false,
  className,
}: CameraPickButtonProps) {
  return (
    <button
      type="button"
      title={title ?? label}
      onClick={onClick}
      className={cn(
        "box-border inline-flex w-full max-w-[11rem] min-w-0 cursor-pointer items-center gap-[0.3rem] overflow-hidden",
        "rounded-md bg-[var(--ui-pe-btn-bg)] px-2 py-[0.2rem] text-[11px] leading-none text-[var(--ui-pe-text)]",
        "transition-colors duration-150 hover:bg-[var(--ui-pe-btn-hover)] hover:text-ui-text-primary",
        reserveClearSpace && "pr-[0.85rem]",
        className,
      )}
    >
      <Video className="h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0 truncate">{label}</span>
    </button>
  )
}

type WithCameraClearProps = {
  children: ReactNode
  showClear: boolean
  onClear: () => void
  clearTitle: string
  className?: string
}

/** Relative wrap + optional clear badge over a camera pick control. */
export function WithCameraClear({ children, showClear, onClear, clearTitle, className }: WithCameraClearProps) {
  return (
    <div className={cn("relative inline-flex max-w-[11rem] min-w-0 shrink-0 items-center", className)}>
      {children}
      {showClear ? <CameraClearButton title={clearTitle} onClick={onClear} /> : null}
    </div>
  )
}
